import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Clock, MapPin, Users, Car } from 'lucide-react';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'http://fleet.host/api'
  : '/api';

interface TripTotals {
  total_trips_distance: number;
  total_trips_duration: string;
  idle_duration: string;
  fuel_consumption: number;
  trips_count: number;
  working_time_duration: string;
  drivers_count: number;
  vehicles_count: number;
}

interface EventStat {
  event_id: number;
  event_name: string;
  num_of_vehicles: number;
}

interface EventStats {
  events: EventStat[];
}

interface AnalyticsData {
  trip_totals: TripTotals;
  event_stats: EventStats;
  from_date: string;
  to_date: string;
}

interface Props {
  fromDate?: string;
  toDate?: string;
  onClose: () => void;
}

const AnalyticsDashboard: React.FC<Props> = ({ fromDate, toDate, onClose }) => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localFromDate, setLocalFromDate] = useState(fromDate || new Date().toISOString().split('T')[0]);
  const [localToDate, setLocalToDate] = useState(toDate || new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadAnalytics();
  }, [localFromDate, localToDate]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({
      from_date: localFromDate,
      to_date: localToDate
    });

    try {
      const response = await fetch(`${API_BASE_URL}/ituran-analytics.php?${params}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to load analytics');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (iso8601: string) => {
    const match = iso8601.match(/P(?:(\d+)D)?T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return iso8601;
    
    const [, days, hours, minutes] = match;
    const parts = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    
    return parts.join(' ') || '0m';
  };

  const topEvents = data?.event_stats.events
    .filter(e => e.num_of_vehicles > 0)
    .sort((a, b) => b.num_of_vehicles - a.num_of_vehicles)
    .slice(0, 10) || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold">Fleet Analytics</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          
          {/* Date Range Picker */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">From:</label>
              <input
                type="date"
                value={localFromDate}
                onChange={(e) => setLocalFromDate(e.target.value)}
                max={localToDate}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">To:</label>
              <input
                type="date"
                value={localToDate}
                onChange={(e) => setLocalToDate(e.target.value)}
                min={localFromDate}
                max={new Date().toISOString().split('T')[0]}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                setLocalFromDate(today);
                setLocalToDate(today);
              }}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                localFromDate === localToDate && localToDate === new Date().toISOString().split('T')[0]
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const lastWeek = new Date(today);
                lastWeek.setDate(today.getDate() - 7);
                const fromDate = lastWeek.toISOString().split('T')[0];
                const toDate = today.toISOString().split('T')[0];
                setLocalFromDate(fromDate);
                setLocalToDate(toDate);
              }}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                (() => {
                  const today = new Date();
                  const lastWeek = new Date(today);
                  lastWeek.setDate(today.getDate() - 7);
                  const expectedFrom = lastWeek.toISOString().split('T')[0];
                  const expectedTo = today.toISOString().split('T')[0];
                  return localFromDate === expectedFrom && localToDate === expectedTo
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200';
                })()
              }`}
            >
              Last 7 Days
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-4 text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && data && (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-700 mb-2">
                    <MapPin className="w-5 h-5" />
                    <span className="text-sm font-medium">Total Distance</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    {data.trip_totals.total_trips_distance.toLocaleString()} km
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <TrendingUp className="w-5 h-5" />
                    <span className="text-sm font-medium">Total Trips</span>
                  </div>
                  <div className="text-2xl font-bold text-green-900">
                    {data.trip_totals.trips_count}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-purple-700 mb-2">
                    <Users className="w-5 h-5" />
                    <span className="text-sm font-medium">Active Drivers</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-900">
                    {data.trip_totals.drivers_count}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-orange-700 mb-2">
                    <Car className="w-5 h-5" />
                    <span className="text-sm font-medium">Active Vehicles</span>
                  </div>
                  <div className="text-2xl font-bold text-orange-900">
                    {data.trip_totals.vehicles_count}
                  </div>
                </div>
              </div>

              {/* Additional Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-700 mb-2">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">Trip Duration</span>
                  </div>
                  <div className="text-xl font-semibold">
                    {formatDuration(data.trip_totals.total_trips_duration)}
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-700 mb-2">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">Working Time</span>
                  </div>
                  <div className="text-xl font-semibold">
                    {formatDuration(data.trip_totals.working_time_duration)}
                  </div>
                </div>

                <div className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-700 mb-2">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">Idle Time</span>
                  </div>
                  <div className="text-xl font-semibold">
                    {formatDuration(data.trip_totals.idle_duration)}
                  </div>
                </div>
              </div>

              {/* Top Events */}
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-4">Top Events</h3>
                <div className="space-y-2">
                  {topEvents.map((event) => (
                    <div key={event.event_id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <span className="text-gray-700">{event.event_name}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${Math.min(100, (event.num_of_vehicles / data.trip_totals.vehicles_count) * 100)}%`
                            }}
                          />
                        </div>
                        <span className="font-semibold text-sm w-8 text-right">
                          {event.num_of_vehicles}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
